import classNames from 'classnames';
import React, { KeyboardEvent, useContext, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';

import { FiltersContext } from './FiltersProvider';

type SearchInputFilterProps = {
    className?: string;
    name: string;
    onChange: Function;
    refresh?: Function;
    placeholder: string;
    disabled?: boolean;
    inputClassName?: string;
    pattern?: RegExp;
    patternErrorMessage?: string;
    onInput?: (e: React.FormEvent<HTMLInputElement>) => void;
};

const SearchInputFilter = ({
    className = '',
    name,
    onChange,
    refresh,
    placeholder,
    disabled = false,
    inputClassName = '',
    pattern,
    patternErrorMessage = '',
    onInput,
}: SearchInputFilterProps) => {
    const filters = useContext(FiltersContext);
    const inputRef = useRef<HTMLInputElement>();
    const [internalError, setInternalError] = useState<boolean>(false);

    const validatePattern = (value: string | null): boolean => {
        if (!pattern || !value || value.trim() === '') {
            return true;
        }

        return pattern.test(value.trim());
    };

    const handleChange = () => {
        const currentValue = inputRef?.current?.value || '';

        if (pattern) {
            const isValid = validatePattern(currentValue);
            setInternalError(!isValid);

            if (!isValid) {
                return;
            }
        }

        if (typeof onChange !== 'undefined') {
            onChange({
                [name]: {
                    value: currentValue,
                },
            });
        }

        if (typeof refresh !== 'undefined') {
            refresh();
        }
    };

    useEffect(() => {
        if (inputRef?.current) {
            inputRef.current.value = filters?.[name]?.value ?? '';
        }
    }, [filters, inputRef]);

    useEffect(() => {
        setInternalError(false);
    }, [filters?.[name]?.value]);

    return (
        <Row className={classNames(className, 'search-input')}>
            <Col lg={11} xs={10} className="px-0 px-lg-4 kt-radius radius">
                <Form.Group className={classNames(inputClassName, 'position-relative')}>
                    <Form.Control
                        ref={inputRef}
                        defaultValue={filters?.[name]?.value ?? ''}
                        onInput={onInput}
                        onKeyUp={(e: KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                handleChange();
                            }
                        }}
                        className="kt-radius-lg-5 kt-radius-0 kt-top-left-radius-5 kt-bottom-left-radius-5"
                        type="text"
                        placeholder={placeholder}
                        disabled={disabled}
                        isInvalid={internalError}
                    />
                    {internalError && patternErrorMessage && (
                        <Form.Control.Feedback className="text-left position-absolute kt-font-sm" type="invalid">
                            {patternErrorMessage}
                        </Form.Control.Feedback>
                    )}
                </Form.Group>
            </Col>
            <Col lg={1} xs={2} className="search-input__button">
                <Button
                    onClick={handleChange}
                    className="btn-icon kt-radius-lg-5 kt-radius-0 kt-top-right-radius-5 kt-bottom-right-radius-5"
                    variant="primary"
                    disabled={disabled}
                >
                    <i className="fas fa-search" />
                </Button>
            </Col>
        </Row>
    );
};

export default SearchInputFilter;
